import {
  CircularProgress,
  IconButton,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { CloseIcon, MagnifyIcon } from '@atgfr8/icons';
import { LoadInputProps, SearchBodyProps } from '@common/types';
import { AutoComplete } from '@components/Autocomplete';
import { autoCompleteMap } from '@components/Header/search/constants';
import { AutoCompleteRenderType } from '@components/Header/search/types';
import { assertAutoCompleteType } from '@components/Header/search/utils';
import { SelectInputCustom } from '@components/Select';
import { FeatureFlags } from '@constants/featureFlags';
import { setDashboardStore } from '@mobileStore/global/store';
import { useNavigate } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { createMemo, Show } from 'solid-js';

const isPickupSearchEnabled = isFeatureFlagEnabled(
  FeatureFlags.feature_ltl_search_pickup_number,
);

export const SearchBody = (props: SearchBodyProps) => {
  const navigate = useNavigate();

  const acConfig = createMemo<AutoCompleteRenderType | undefined>(
    () => autoCompleteMap[props.selectedCategory()],
  );

  // Same flag that unlocks Pickup# search also moves Pro# onto the unified endpoint.
  // acConfig() is undefined for the 'Load' category (handled by LoadInput, not
  // CategoryAutoComplete) — this memo still runs eagerly on every category
  // change, so it must not assume acConfig() exists.
  const resolvedEndpoint = createMemo(() => {
    if (props.selectedCategory() === 'Pro #' && isPickupSearchEnabled()) {
      return 'Order/referencenumbertype?type=Pro&query';
    }
    return acConfig()?.endpoint;
  });

  // The unified reference-number endpoint (Pickup# always, Pro# only once the
  // flag moves it over) only supports exact matches, unlike every other
  // search's partial match.
  const isExactMatchMode = createMemo(
    () =>
      props.selectedCategory() === 'Pickup #' ||
      (props.selectedCategory() === 'Pro #' && isPickupSearchEnabled()),
  );

  // Exact-match searches start at two characters; Autocomplete uses a strict
  // greater-than check for its threshold. Only the legacy partial-match
  // Pro# path keeps its configured minimum.
  const resolvedMinCharacters = createMemo(() =>
    isExactMatchMode() ? 1 : (acConfig()?.minCharacters ?? 2),
  );

  const handleTextInput = (e: InputEvent) => {
    props.setText((e.target as HTMLInputElement).value);
    props.setSearchError(null);
    setDashboardStore('globalSearchHasError', false);
  };

  const onEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') void props.handleLoadSearch();
  };

  const LoadInput = (p: LoadInputProps) => (
    <TextField
      dataTestId={p.dataTestId}
      label=""
      style={{
        background: 'white',
        'border-radius': '5px',
        'margin-left': '2px',
      }}
      class={p.class}
      value={props.text() ?? ''}
      on:input={handleTextInput}
      onKeyPress={onEnter}
      placeholder="Search..."
      endIcon={
        <Show when={props.isLoading()}>
          <div class="pr-2">
            <CircularProgress size={p.spinner} />
          </div>
        </Show>
      }
    />
  );

  const CategoryAutoComplete = (p: {
    listClass: string;
    closeOnSelect: boolean;
    fitContent?: boolean;
  }) => (
    <AutoComplete<AutoCompleteRenderType>
      label=""
      placeholder="Search..."
      // CategoryAutoComplete only renders when selectedCategory !== 'Load'
      // (see the Show below), so acConfig() is guaranteed defined here.
      version={acConfig()!.version}
      endpoint={resolvedEndpoint()!}
      id={acConfig()!.id}
      dropdownFitContent={p.fitContent}
      clearSearch={props.clearSearch()}
      phoneInput={props.selectedCategory() === 'Phone #'}
      onChange={() => props.clearSearch() && props.setClearSearch(false)}
      renderItem={(item) =>
        assertAutoCompleteType(
          item,
          props.selectedCategory() as keyof typeof autoCompleteMap,
          'display',
        )
      }
      minCharacters={resolvedMinCharacters()}
      exactMatchField={isExactMatchMode() ? 'referenceNumber' : undefined}
      noExactMatchMessage={
        isExactMatchMode()
          ? `No exact match found. An exact match is needed when searching by ${props.selectedCategory()}.`
          : undefined
      }
      onItemSelect={(item) => {
        const link = assertAutoCompleteType(
          item,
          props.selectedCategory() as keyof typeof autoCompleteMap,
          'redirect',
        );
        if (link !== '') {
          navigate(link as string);
          if (p.closeOnSelect) props.onClose?.();
        }
      }}
      class="bg-white! rounded-[5px]"
      listClass={p.listClass}
    />
  );

  const CloseButton = () => (
    <IconButton
      dataTestId={formatDataTestId('Close Search', 'button')}
      aria-label="Close search"
      onClick={props.onClose}
      class="rounded-lg! text-white! shrink-0! bg-[#B00020]"
    >
      <CloseIcon size="20px" color="white" />
    </IconButton>
  );

  return (
    <div class="flex items-center gap-1">
      <div class={props.isMobile ? 'shrink-0 w-[90px]!' : 'shrink-0'}>
        <SelectInputCustom
          label=""
          class={`h-[36px]! ${props.isMobile ? '' : 'min-w-[140px]!'}`}
          menuItems={props.menuItems()}
          value={props.selectedCategory()}
          renderValue={(value: string | string[]): string =>
            Array.isArray(value) ? value.join(', ') : value
          }
          onChange={props.handleChange}
          dataTestId="search-category-select"
          menuClass="min-w-[320px]"
          menuItemClass="group hover:bg-[#0a2634]! py-0.5!"
        />
      </div>

      <div class="min-w-0 flex-1">
        <Show
          when={props.selectedCategory() === 'Load'}
          fallback={
            <Show
              when={props.isMobile}
              fallback={
                <CategoryAutoComplete
                  listClass="max-h-[600px]! overflow-auto!"
                  closeOnSelect={false}
                  fitContent
                />
              }
            >
              <div class="flex items-center gap-1">
                <div class="min-w-0 flex-1">
                  <CategoryAutoComplete
                    listClass="max-h-[400px]! overflow-auto!"
                    closeOnSelect={true}
                  />
                </div>
                <CloseButton />
              </div>
            </Show>
          }
        >
          <Show
            when={props.isMobile}
            fallback={
              <LoadInput
                dataTestId="search-field-textInput"
                class="h-9!"
                spinner={20}
              />
            }
          >
            <div class="flex items-center gap-1">
              <LoadInput
                dataTestId="mobile-search-load-input"
                class="flex-1 h-9!"
                spinner={16}
              />
              <IconButton
                dataTestId={formatDataTestId('Search', 'button')}
                aria-label="Search"
                onClick={() => void props.handleLoadSearch()}
                disabled={props.isLoading() || !props.text()}
                class="rounded-lg! text-white! shrink-0!"
                style={{ background: '#1b4960' }}
              >
                <MagnifyIcon size="20px" color="white" />
              </IconButton>
              <CloseButton />
            </div>
          </Show>
        </Show>
      </div>
    </div>
  );
};
